import { CoveragePriceResult } from "@redotech/redo-model/coverage-calculator";
import { getInternationalPrice } from "@redotech/redo-model/coverage-calculator/country-check";
import { calculateDynamicReturnsPrice } from "@redotech/redo-model/coverage-calculator/utils/calculate-dynamic-returns-price";
import { getFinalSaleReturnsPrice } from "@redotech/redo-model/coverage-calculator/utils/get-final-sale-returns-price";
import {
  getCoverageMlModelInference,
  getPriceBracketPrice,
} from "@redotech/redo-model/coverage-calculator/utils/get-price-bracket-price";
import { CoveragePriceCacheValue } from "@redotech/redo-model/storefront/cart-attributes";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { ShopifyCartItem } from "@redotech/shopify-client/ajax";

export function getCalculatedCoveragePrice({
  basePriceDollars,
  autocheck,
  onTapcart,
  cartValueReturnableItems,
  cartValuePackageProtectionItems,
  finalSaleReturnsCartValue,
  cartItems,
  numValidReturnProducts,
  country,
  cartHasSubscriptionItem,
  coveragePriceCacheValue,
  priceCalculationContext,
}: {
  basePriceDollars: number;
  autocheck: WidgetAutocheck;
  onTapcart: boolean;
  cartValueReturnableItems: number;
  cartValuePackageProtectionItems: number;
  finalSaleReturnsCartValue: number;
  cartItems: ShopifyCartItem[] | undefined;
  numValidReturnProducts: number;
  country?: string | null;
  cartHasSubscriptionItem: boolean;
  coveragePriceCacheValue: CoveragePriceCacheValue | undefined;
  priceCalculationContext?: { checkoutProbability: number | undefined };
}): {
  returnCost: number;
  packageProtectionCost: number;
  bothCost: number;
  rawCoveragePrices: {
    returns: CoveragePriceResult | undefined;
    packageProtection: CoveragePriceResult | undefined;
    finalSaleReturns: CoveragePriceResult | undefined;
  };
} {
  let returnCost = 0;

  // tapcart does everything in dollars
  const basePriceCents = onTapcart ? basePriceDollars : basePriceDollars * 100;

  const returnCartValue = onTapcart
    ? cartValueReturnableItems
    : cartValueReturnableItems / 100;

  const fsrCartValue = onTapcart
    ? finalSaleReturnsCartValue
    : finalSaleReturnsCartValue / 100;

  if (autocheck.dynamicReturnsPricing && cartItems) {
    returnCost = calculateDynamicReturnsPrice(
      autocheck.dynamicReturnsPricing,
      basePriceDollars,
      numValidReturnProducts,
      returnCartValue,
    ).price;
    returnCost = onTapcart ? returnCost : returnCost * 100;
  } else {
    returnCost = autocheck.pricePerOrder
      ? onTapcart
        ? parseFloat(autocheck.pricePerOrder)
        : parseFloat(autocheck.pricePerOrder) * 100
      : basePriceCents; // realistically we should always have a "pricePerOrder" so we shouldn't reach this.
  }

  if (country && autocheck.internationalReturnsPricing) {
    returnCost = getInternationalPrice(
      returnCost,
      country,
      autocheck.countries,
      autocheck.internationalReturnsPricing,
    );
  }

  if (autocheck.finalSaleReturns.coverageEnabled) {
    let { price: finalSaleReturnsCost } = getFinalSaleReturnsPrice({
      cartTotal: fsrCartValue,
      pricingRuleSets: autocheck.finalSaleReturns.pricingRuleSets,
      shippingLabelCoveragePrice:
        autocheck.finalSaleReturns.shippingLabelCoveragePrice,
      country,
      minPrice: autocheck.finalSaleReturns.minPrice,
      maxPrice: autocheck.finalSaleReturns.maxPrice,
      backupPercentage: autocheck.finalSaleReturns.percentage,
    });
    finalSaleReturnsCost = onTapcart
      ? finalSaleReturnsCost
      : finalSaleReturnsCost * 100;

    // Since final sale returns price bakes in the price for returns, we can simply take
    // the max of the two prices to determine how much to charge for returns on mixed carts.
    // Final sale returns does not support dynamic pricing
    returnCost = Math.max(returnCost, finalSaleReturnsCost);
  }

  // tapcart works in dollars so we don't want to round to the nearest dollar
  if (!onTapcart) {
    returnCost = Math.round(returnCost);
  }

  const packageProtectionCartValue = onTapcart
    ? cartValuePackageProtectionItems
    : cartValuePackageProtectionItems / 100;

  const coverageMlModel = getCoverageMlModelInference({
    mlModel: autocheck.packageProtection.coverageMlModel ?? undefined,
    sessionInferenceParams: {
      checkoutProbability: priceCalculationContext?.checkoutProbability,
    },
  });

  const { packageProtectionCost, rawPackageProtectionPriceResult } = (() => {
    const priceResult = getPriceBracketPrice({
      cartTotal: packageProtectionCartValue,
      pricingRuleSets: autocheck.packageProtection.pricingRuleSets,
      country,
      minPrice: autocheck.packageProtection.minPrice,
      maxPrice: autocheck.packageProtection.maxPrice,
      backupPercentage: autocheck.packageProtection.percentage,
      fixedSubscriptionPrice:
        autocheck.packageProtection.fixedSubscriptionPrice,
      cartHasSubscriptionItem,
      coverageMlModel,
      priceOverrideForCoverageMlModelCacheOnly:
        coveragePriceCacheValue?.packageProtection,
    });
    if (onTapcart) {
      return {
        packageProtectionCost: priceResult.price,
        rawPackageProtectionPriceResult: priceResult,
      };
    }
    return {
      packageProtectionCost: Math.round(priceResult.price * 100), // round to nearest cent
      rawPackageProtectionPriceResult: priceResult,
    };
  })();

  return {
    returnCost,
    packageProtectionCost,
    bothCost: packageProtectionCost + returnCost,
    rawCoveragePrices: {
      returns: undefined, // unimplemented
      packageProtection: rawPackageProtectionPriceResult,
      finalSaleReturns: undefined, // unimplemented
    },
  };
}
